import { compterParStatut, trierLignes } from '@imp/shared/liste-triee';
import { messageErreur } from '@imp/shared/message-erreur';
import { filtrerRecherche } from '@imp/shared/recherche';
import { useQuery } from '@tanstack/react-query';
import { ListChecks, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { BarreRecherche } from '@/components/BarreRecherche';
import { EnteteTriable } from '@/components/EnteteTriable';
import { PastillesStatut } from '@/components/PastillesStatut';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useEtatListe } from '@/lib/useEtatListe';
import { formatEuro } from '@/modules/catalogue/api';
import { contactDisplayName, listContacts } from '@/modules/clients/api';
import { DEVIS_STATUT_LABELS, type DevisListItem, listDevis } from '@/modules/devis/api';

const STATUT_CLASSES: Record<string, string> = {
  brouillon: 'bg-muted text-muted-foreground',
  a_envoyer: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  envoye: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  consulte: 'bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  signe: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  converti_facture: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  refuse: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  expire: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  annule: 'bg-muted text-muted-foreground',
};

/** L'ordre dans lequel on traite un devis : c'est aussi celui des pastilles. */
const ORDRE_STATUTS = Object.keys(DEVIS_STATUT_LABELS);

const CLES_TRI = ['numero', 'client', 'objet', 'date', 'statut', 'ttc'] as const;
type CleTri = (typeof CLES_TRI)[number];

/** `2026-10-06` → `06/10/2026` */
function dateFr(iso: string | null): string {
  return iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString('fr-FR') : '—';
}

export function DevisListPage() {
  const [q, setQ] = useState('');
  // Par défaut, le plus récent en tête.
  const liste = useEtatListe<CleTri>(CLES_TRI, { cle: 'date', sens: 'desc' });
  const devisQ = useQuery({ queryKey: ['devis'], queryFn: listDevis });
  const contactsQ = useQuery({ queryKey: ['contacts'], queryFn: listContacts });
  const contactMap = useMemo(
    () => new Map((contactsQ.data ?? []).map((c) => [c.id, c])),
    [contactsQ.data],
  );
  const devis = useMemo(() => devisQ.data ?? [], [devisQ.data]);
  // Recherche universelle : on fouille tout ce que la ligne affiche.
  const filtered = useMemo(
    () =>
      filtrerRecherche(devis, q, (d) => {
        const c = contactMap.get(d.contact_id);
        return [
          d.numero,
          c ? contactDisplayName(c) : null,
          d.objet,
          d.date_emission,
          DEVIS_STATUT_LABELS[d.statut] ?? d.statut,
        ];
      }),
    [devis, q, contactMap],
  );
  const nomClient = (d: DevisListItem): string => {
    const c = contactMap.get(d.contact_id);
    return c ? contactDisplayName(c) : '';
  };
  // Les pastilles comptent ce que la recherche a gardé : elles disent ce qu'on
  // obtiendrait en cliquant.
  const comptes = compterParStatut(filtered, (d) => d.statut, ORDRE_STATUTS);
  const affiches = trierLignes(
    liste.statut === null ? filtered : filtered.filter((d) => d.statut === liste.statut),
    liste.tri,
    {
      numero: (d) => d.numero,
      client: nomClient,
      objet: (d) => d.objet,
      date: (d) => d.date_emission,
      statut: (d) => ORDRE_STATUTS.indexOf(d.statut),
      ttc: (d) => Number(d.montant_ttc),
    },
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Devis</h1>
          <p className="text-muted-foreground">Établissez et suivez vos devis.</p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <Button asChild variant="outline">
            <Link to="/devis/catalogue">
              <ListChecks className="h-4 w-4" /> Devis express
            </Link>
          </Button>
          <Button asChild>
            <Link to="/devis/nouveau">
              <Plus className="h-4 w-4" /> Nouveau devis
            </Link>
          </Button>
        </div>
      </div>

      {devis.length > 0 ? (
        <BarreRecherche
          valeur={q}
          onChange={setQ}
          quoi="un devis"
          champs="numéro, client, objet, statut"
          resultats={filtered.length}
          total={devis.length}
        />
      ) : null}
      {devis.length > 0 ? (
        <PastillesStatut
          comptes={comptes}
          libelles={DEVIS_STATUT_LABELS}
          classes={STATUT_CLASSES}
          actif={liste.statut}
          total={filtered.length}
          onChoisir={liste.choisirStatut}
        />
      ) : null}

      {devisQ.isLoading ? (
        <p className="text-muted-foreground">Chargement…</p>
      ) : devisQ.isError ? (
        <p className="text-destructive">Erreur : {messageErreur(devisQ.error)}</p>
      ) : affiches.length === 0 ? (
        <Card className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <p className="text-muted-foreground">
            {devis.length === 0 ? 'Aucun devis pour le moment.' : 'Aucun résultat.'}
          </p>
          {devis.length === 0 ? (
            <Button asChild variant="outline">
              <Link to="/devis/nouveau">
                <Plus className="h-4 w-4" /> Créer le premier devis
              </Link>
            </Button>
          ) : null}
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <EnteteTriable cle="numero" libelle="Numéro" tri={liste.tri} onTrier={liste.trierPar} sensNaturel="desc" />
                <EnteteTriable cle="client" libelle="Client" tri={liste.tri} onTrier={liste.trierPar} />
                <EnteteTriable cle="objet" libelle="Objet" tri={liste.tri} onTrier={liste.trierPar} />
                <EnteteTriable cle="date" libelle="Date" tri={liste.tri} onTrier={liste.trierPar} sensNaturel="desc" />
                <EnteteTriable cle="statut" libelle="Statut" tri={liste.tri} onTrier={liste.trierPar} />
                <EnteteTriable cle="ttc" libelle="Total TTC" tri={liste.tri} onTrier={liste.trierPar} sensNaturel="desc" aDroite />
              </tr>
            </thead>
            <tbody>
              {affiches.map((d) => {
                const c = contactMap.get(d.contact_id);
                return (
                  <tr key={d.id} className="border-b last:border-0 hover:bg-muted/40">
                    <td className="px-4 py-3 font-mono text-xs">
                      <Link to={`/devis/${d.id}`} className="text-primary hover:underline">
                        {d.numero}
                      </Link>
                    </td>
                    <td className="px-4 py-3">{c ? contactDisplayName(c) : '—'}</td>
                    <td className="px-4 py-3 text-muted-foreground">{d.objet}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-muted-foreground">
                      {dateFr(d.date_emission)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-medium ${STATUT_CLASSES[d.statut] ?? 'bg-muted text-muted-foreground'}`}
                      >
                        {DEVIS_STATUT_LABELS[d.statut] ?? d.statut}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-medium">{formatEuro(d.montant_ttc)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
